import type { Section, FormValues, FormInstance } from '../types';
import { getSectionStatus } from './sectionStatus';
import { parseFirstIncompleteIndex } from './sectionUtils';
import { DictionaryT } from './sectionStatus';

const SECTION_LIST_STEP = 'step-3-sections';

/**
 * Checks if the current section ID represents an active section (not the section list)
 */
export function isActiveSection(currentSectionId: string | null): boolean {
  return Boolean(currentSectionId && currentSectionId !== SECTION_LIST_STEP);
}

/**
 * Checks if we're on the section list step
 */
export function isSectionListStep(currentSectionId: string | null): boolean {
  return currentSectionId === SECTION_LIST_STEP || !currentSectionId;
}

/**
 * Finds the index of an active section in the visible sections array
 */
export function findActiveSectionIndex(
  currentSectionId: string,
  visibleSections: Section[]
): number {
  return visibleSections.findIndex((section) => {
    // Check if it's the section itself
    if (section.id === currentSectionId) {
      return true;
    }
    // Check if it's a card within this section
    if (section.cards && section.cards.some((card) => card.id === currentSectionId)) {
      return true;
    }
    return false;
  });
}

/**
 * Finds the index of the last completed section
 */
export function findLastCompletedSectionIndex(
  visibleSections: Section[],
  completedForms: Record<string, FormInstance[]>,
  currentSectionId: string | null,
  values: FormValues,
  localStorageKey?: string,
  getDictionaryValue?: DictionaryT
): number {
  for (let i = visibleSections.length - 1; i >= 0; i--) {
    const section = visibleSections[i];
    const sectionStatus = getSectionStatus(
      section,
      completedForms,
      currentSectionId,
      values,
      localStorageKey,
      getDictionaryValue
    );
    if (getDictionaryValue && sectionStatus === getDictionaryValue('Complete', 'Complete')) {
      return i;
    }
  }
  return -1;
}

/**
 * Calculates the current section index based on various conditions
 */
export function calculateCurrentSectionIndex(
  currentSectionId: string | null,
  visibleSections: Section[],
  firstIncompleteIndex: string | number,
  completedForms: Record<string, FormInstance[]>,
  values: FormValues,
  localStorageKey?: string,
  getDictionaryValue?: DictionaryT
): number {
  // First, try to find the current section by ID (but not if it's step-3-sections)
  if (isActiveSection(currentSectionId)) {
    const currentIndex = findActiveSectionIndex(currentSectionId!, visibleSections);
    if (currentIndex >= 0) {
      return currentIndex;
    }
  }

  // When on step-3-sections, find the last completed section index
  // This allows handleNext to correctly navigate to currentSectionIndex + 1
  if (isSectionListStep(currentSectionId)) {
    const lastCompletedIndex = findLastCompletedSectionIndex(
      visibleSections,
      completedForms,
      currentSectionId,
      values,
      localStorageKey,
      getDictionaryValue
    );

    // If we found a completed section, use its index
    // handleNext will then navigate to lastCompletedIndex + 1
    if (lastCompletedIndex >= 0) {
      return lastCompletedIndex;
    }

    // If no completed sections, fallback to first incomplete index - 1
    // This ensures handleNext navigates to the first incomplete section
    const parsedIndex = parseFirstIncompleteIndex(firstIncompleteIndex);
    return parsedIndex > 0 ? parsedIndex - 1 : 0;

    // Note: There's unreachable code here (line 109 in original) that would return
    // second-to-last section if all sections are complete. This is never reached
    // because the condition above handles it. Keeping original logic structure.
  }

  // If no current section, use first incomplete index
  const parsedIndex = parseFirstIncompleteIndex(firstIncompleteIndex);
  return parsedIndex >= 0 ? parsedIndex : 0;
}
